import { Select, Input, IconButton, Popover } from '@ltvco/refresh-lib/theme';
import { Close } from '@mui/icons-material';
import React, { useState, useCallback, useEffect } from 'react';

import {
  PopoverContainer,
  Header,
  Title,
  ContentBody,
  FieldContainer,
  FieldLabel,
  selectStyles,
  inputStyles,
  Footer,
  ApplyButton,
  CancelButton,
} from './components';

// ============= TYPES =============

/** Text input field configuration */
export interface MoreTextFieldConfig {
  id: string;
  label: string;
  type: 'text';
  placeholder?: string;
}

/** Select/dropdown field configuration */
export interface MoreSelectFieldConfig {
  id: string;
  label: string;
  type: 'select';
  options: Array<{ value: string; label: string }>;
  defaultValue?: string;
}

/** Union type for field configurations */
export type MoreFieldConfig = MoreTextFieldConfig | MoreSelectFieldConfig;

/** Configuration for the More section */
export interface MoreSectionConfig {
  id: string;
  label: string;
  popoverTitle?: string;
  fields: MoreFieldConfig[];
}

/** Data returned from the More filters popover */
export type MoreFiltersData = Record<string, string>;

/** Legacy type for backward compatibility */
export interface OrderByOption {
  value: string;
  label: string;
}

export interface MoreFiltersPopoverProps {
  /** Anchor element for the popover */
  anchorEl: HTMLElement | null;
  /** Whether the popover is open */
  open: boolean;
  /** Callback when popover is closed */
  onClose: () => void;
  /** Configuration for the More section fields */
  config: MoreSectionConfig;
  /** Currently selected filter values */
  currentValues: MoreFiltersData;
  /** Callback when filters are applied */
  onApply: (filters: MoreFiltersData) => void;
  /** Test ID for the component */
  testId?: string;
}

// ============= COMPONENT =============
export const MoreFiltersPopover: React.FC<MoreFiltersPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  config,
  currentValues,
  onApply,
  testId = 'more-filters-popover',
}) => {
  const [localValues, setLocalValues] =
    useState<MoreFiltersData>(currentValues);

  // Reset local values when popover opens
  useEffect(() => {
    if (open) {
      setLocalValues(currentValues);
    }
  }, [open, currentValues]);

  // Handle field value change
  const handleFieldChange = useCallback((fieldId: string, value: string) => {
    setLocalValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(localValues);
    onClose();
  }, [localValues, onApply, onClose]);

  const handleCancel = useCallback(() => {
    setLocalValues(currentValues);
    onClose();
  }, [currentValues, onClose]);

  // Render a field based on its configuration
  const renderField = (fieldConfig: MoreFieldConfig) => {
    switch (fieldConfig.type) {
      case 'select': {
        const selectOptions = fieldConfig.options.map((option) => ({
          value: option.value,
          display: option.label,
        }));
        const currentValue =
          localValues[fieldConfig.id] || fieldConfig.defaultValue || '';

        return (
          <FieldContainer key={fieldConfig.id}>
            <FieldLabel>{fieldConfig.label}</FieldLabel>
            <Select
              id={`${fieldConfig.id}-select`}
              options={selectOptions}
              onOptionSelect={(value) =>
                handleFieldChange(fieldConfig.id, value)
              }
              value={currentValue}
              placeholder={`Select ${fieldConfig.label.toLowerCase()}`}
              sx={selectStyles}
            />
          </FieldContainer>
        );
      }
      case 'text': {
        const currentValue = localValues[fieldConfig.id] || '';

        return (
          <FieldContainer key={fieldConfig.id}>
            <FieldLabel>{fieldConfig.label}</FieldLabel>
            <Input
              id={`${fieldConfig.id}-input`}
              type="text"
              value={currentValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFieldChange(fieldConfig.id, e.target.value)
              }
              placeholder={
                fieldConfig.placeholder ||
                `Enter ${fieldConfig.label.toLowerCase()}`
              }
              sx={inputStyles}
              fullWidth
            />
          </FieldContainer>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      marginThreshold={8}
      slotProps={{
        paper: {
          sx: (theme) => ({
            mt: 1,
            maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' },
            [theme.breakpoints.only('xs')]: {
              left: '8px',
              right: '8px',
            },
          }),
        },
      }}
      data-testid={testId}
    >
      <PopoverContainer>
        {/* Header */}
        <Header>
          <Title variant="h6">
            {config.popoverTitle || 'Add More Details'}
          </Title>
          <IconButton size="small" onClick={handleCancel} aria-label="Close">
            <Close />
          </IconButton>
        </Header>

        {/* Content Body - Dynamic Fields */}
        <ContentBody>{config.fields.map(renderField)}</ContentBody>

        {/* Footer */}
        <Footer>
          <ApplyButton variant="contained" onClick={handleApply}>
            Apply
          </ApplyButton>
          <CancelButton variant="outlined" onClick={handleCancel}>
            Cancel
          </CancelButton>
        </Footer>
      </PopoverContainer>
    </Popover>
  );
};
